import React, { useState, useRef } from 'react';
import { QrCode, X, ExternalLink, Printer } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { CLINIC_NAME } from '../utils/clinicConfig';

export default function QRDisplay({ portalUrl, patientUID }) {
  const [open, setOpen]   = useState(false);
  const qrRef             = useRef(null);

  const url = patientUID
    ? `${portalUrl || window.location.origin}/portal?uid=${patientUID}`
    : `${portalUrl || window.location.origin}/portal`;

  function handlePrint() {
    // Serialize the QR SVG already rendered in the DOM — no CDN needed
    const svgEl  = qrRef.current?.querySelector('svg');
    const svgStr = svgEl ? new XMLSerializer().serializeToString(svgEl) : '';
    const w = window.open('', '_blank');
    if (!w) { alert('Please allow popups to print the QR code.'); return; }
    w.document.write(`<!DOCTYPE html>
<html><head><title>QR Check-in — ${CLINIC_NAME}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, sans-serif; text-align: center; padding: 48px 32px; background: white; }
  .qr-wrap { display: inline-block; padding: 16px; border: 2px solid #e2e8f0; border-radius: 12px; margin-bottom: 20px; }
  svg { display: block; }
  h2  { font-size: 22px; font-weight: 800; color: #0a1628; margin: 0 0 6px; }
  p   { color: #64748b; font-size: 14px; margin: 4px 0; }
  .url{ color: #94a3b8; font-size: 11px; margin-top: 12px; word-break: break-all; }
  .instructions { margin-top: 20px; text-align: left; display: inline-block; max-width: 300px; }
  .instructions li { font-size: 13px; color: #475569; margin-bottom: 4px; }
  @media print { @page { margin: 20mm; } }
</style></head><body>
  <div class="qr-wrap">${svgStr}</div>
  <h2>${CLINIC_NAME}</h2>
  <p style="font-weight:700">Scan to check your queue token status</p>
  <p>No app download needed</p>
  <ol class="instructions">
    <li>Open your phone camera and point at the QR code</li>
    <li>Tap the link that appears on screen</li>
    <li>Enter your registered mobile number</li>
    <li>See your token number and queue position live</li>
  </ol>
  <div class="url">${url}</div>
  <script>window.onload = function(){ window.print(); }</script>
</body></html>`);
    w.document.close();
  }

  return (
    <>
      <button onClick={() => setOpen(true)} className="btn btn-outline btn-sm"
        style={{ display:'flex', alignItems:'center', gap:6 }} title="Show QR Code for patient check-in">
        <QrCode size={14} /> QR Check-in
      </button>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <div className="modal" style={{ maxWidth:360, padding:28, textAlign:'center' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:16 }}>Patient QR Check-in</div>
              <button className="btn btn-icon btn-ghost" onClick={() => setOpen(false)}><X size={18}/></button>
            </div>

            {/* QR rendered client-side — ref used for print */}
            <div ref={qrRef} style={{ display:'inline-block', padding:16, background:'white', borderRadius:12, border:'2px solid #e2e8f0', marginBottom:16 }}>
              <QRCodeSVG value={url} size={200} bgColor="#ffffff" fgColor="#0a1628" level="M" includeMargin={false} />
            </div>

            <div style={{ fontSize:14, fontWeight:700, color:'var(--color-ink)', marginBottom:4 }}>{CLINIC_NAME}</div>
            <div style={{ fontSize:12, color:'var(--color-ink-soft)', marginBottom:16, lineHeight:1.5 }}>
              Patients scan this QR with their phone camera to check their queue status — no app download needed.
            </div>

            <div style={{ padding:'8px 12px', background:'var(--color-surface)', borderRadius:8, fontSize:11, color:'var(--color-ink-soft)', wordBreak:'break-all', marginBottom:16, textAlign:'left', border:'1px solid var(--color-border)' }}>
              {url}
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button className="btn btn-primary" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }} onClick={handlePrint}>
                <Printer size={14}/> Print QR
              </button>
              <a href={url} target="_blank" rel="noopener noreferrer"
                className="btn btn-outline" style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                <ExternalLink size={14}/> Open Page
              </a>
            </div>
            <div style={{ fontSize:11, color:'var(--color-ink-faint)', marginTop:12 }}>
              Display this QR at your reception desk.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
